import { useEffect, useRef } from "react";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { useTransport } from "../hooks/useTransport";
import { PlayIcon, PauseIcon, MetronomeIcon, MinusIcon, PlusIcon } from "./icons";
import { ChordName } from "./ChordName";
import { useT } from "../i18n";
import { useUi } from "../state/ui";
import { useKeyStore } from "../state/keyStore";
import { useChordView } from "../hooks/useChordView";
import { useStyledChart } from "../hooks/useStyledChart";
import { measureSegments } from "../core/grid/segments";

// Floating bottom play dock (thumb zone): progress · current chord (large) + next chord ·
// metronome · play. With the metronome over the song, a click-timing stepper sits right here so the
// offset can be nudged while listening.
export function PlayDock() {
  const t = useT();
  const measures = useProjectStore((s) => s.chartMeasures);
  const isPlaying = usePlaybackStore((s) => s.isPlaying);
  const currentMeasure = usePlaybackStore((s) => s.currentMeasure);
  const currentBeatTime = usePlaybackStore((s) => s.currentBeatTime);
  const { toggle, metronome, cycleMetronome } = useTransport();
  // chords follow the score on screen: the user's PDF can't be transposed, so under it the dock stays in the original key
  const pdf = useUi((s) => s.scoreView === "score");
  const transpose = useKeyStore((s) => s.transpose);
  const capo = useKeyStore((s) => s.capo);
  const view = useChordView(pdf);
  const chart = useStyledChart();
  if (!measures.length) return null;

  // chord under the cursor and the next DIFFERENT chord after it (beat-accurate, same source as the grid).
  // Under the chord chart the play style names them (D/F#, Cadd9…); under the user's PDF the plain names.
  const named = (m: number, s: number, label: string) => {
    const shown = pdf ? null : chart.at(m, s);
    return shown ? { name: shown.name, added: shown.added } : { name: view.name(label), added: "" };
  };
  const mi = Math.max(0, currentMeasure);
  const beats = measures[mi].beats;
  let bi = 0;   // index of the beat under the cursor within the current measure
  if (currentMeasure >= 0) beats.forEach((b, j) => { if (currentBeatTime >= b.time - 0.02) bi = j; });
  const segs = measureSegments(measures[mi]);
  let si = 0;   // segment holding that beat
  for (let k = 0, n = 0; k < segs.length; n += segs[k].beats, k++) if (bi >= n) si = k;
  const nowLabel = beats[bi]?.chord ?? null;
  const now = nowLabel ? named(mi, si, nowLabel) : null;
  let next: { name: string; added: string } | null = null;
  outer: for (let i = mi, j = si + 1; i < measures.length; i++, j = 0) {
    const ss = measureSegments(measures[i]);
    for (; j < ss.length; j++) {
      const ch = ss[j].chord;
      if (!ch) continue;
      const nm = named(i, j, ch);
      if (nm.name !== now?.name) { next = nm; break outer; }
    }
  }
  const progress = currentMeasure < 0 ? 0 : (currentMeasure + 1) / measures.length;

  return (
    <div className="play-dock">
      <div className="pd-progress" aria-hidden="true"><i style={{ transform: `scaleX(${progress})` }} /></div>
      <div className="pd-row">
        <div className="pd-chords" aria-live="polite">
          <span className="pd-now">{now ? <ChordName chord={now.name} added={now.added} /> : "—"}</span>
          {next && <span className="pd-next"><small>{t("song.next")}</small><ChordName chord={next.name} added={next.added} /></span>}
        </div>
        <button className={"pd-metro" + (metronome !== "off" ? " " + metronome : "")} onClick={cycleMetronome}
          aria-label={t("metro." + metronome)} title={t("metro." + metronome)}>
          <MetronomeIcon size={18} />
          {metronome !== "off" && <span>{t("metro.tag_" + metronome)}</span>}
        </button>
        <button className="pd-play" onClick={toggle} title={t("ctrl.play")} aria-label={t("ctrl.play")}>
          {isPlaying ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
        </button>
      </div>
      <div className="pd-meta">
        <span className="mono">{currentMeasure >= 0 ? currentMeasure + 1 : 1} / {measures.length}</span>
        {/* beats of the current bar (first = downbeat): compare against the clicks / the song */}
        <span className="pd-beats" aria-hidden="true">
          {beats.map((_, j) => <i key={j} className={(j === 0 ? "down" : "") + (currentMeasure >= 0 && j === bi ? " on" : "")} />)}
        </span>
        {pdf ? (transpose !== 0 || capo > 0) && <span className="pd-key orig">{t("key.orig_tag")}</span>
          : capo > 0 && <span className="pd-key">{t("key.capo_tag", { n: capo })}</span>}
        {metronome === "overlay" && <MetroTiming />}
      </div>
    </div>
  );
}

const STEP = 5;   // ms per tap

// − value + : nudges the click offset (+ = earlier). Tap = one step, hold = repeat; tap the value to reset.
function MetroTiming() {
  const t = useT();
  const offset = usePlaybackStore((s) => s.metroOffset);
  const timer = useRef<number | null>(null);
  const stop = () => { if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => stop, []);
  const bump = (d: number) => {
    const pb = usePlaybackStore.getState();
    pb.setMetroOffset(pb.metroOffset + d);
  };
  // hold: first repeat after 400 ms, then every 70 ms
  const press = (d: number) => {
    stop();
    bump(d);
    const loop = (delay: number) => { timer.current = window.setTimeout(() => { bump(d); loop(70); }, delay); };
    loop(400);
  };
  const hold = (d: number) => ({
    onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); press(d); },
    onPointerUp: stop, onPointerLeave: stop, onPointerCancel: stop,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); bump(d); } },
  });
  return (
    <span className="pd-timing" role="group" aria-label={t("metro.timing")}>
      <span className="pd-timing-label">{t("metro.timing")}</span>
      <button className="pd-step" {...hold(-STEP)} aria-label={t("metro.later")} title={t("metro.later")}><MinusIcon size={14} /></button>
      <button className={"pd-offset mono" + (offset ? " set" : "")} onClick={() => usePlaybackStore.getState().setMetroOffset(0)}
        title={t("metro.timing_reset")} aria-label={`${t("metro.timing")} ${offset}ms — ${t("metro.timing_reset")}`}>
        {offset > 0 ? "+" : ""}{offset}ms
      </button>
      <button className="pd-step" {...hold(STEP)} aria-label={t("metro.earlier")} title={t("metro.earlier")}><PlusIcon size={14} /></button>
    </span>
  );
}
